import { Check, CheckCircle2, CircleOff, ListRestart, Minus, Save, ShieldCheck, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { Button, CheckboxField, SelectField, StatusMessage } from '@krp/ui-kit'

import { ActionReason, CommandError } from '../../resources/ui/ActionReason'
import { QueryState } from '../../../shared/QueryState'
import type { AccessActions, AccessVm, CapabilityRefVm, MyAccessVm, RolesSummaryVm } from '../model/types'
import '../../../shared/platform.css'

/**
 * Права к ресурсам (RP-A3): какие ресурсы допущены роли и какие права вида у
 * неё есть. Правит только администратор приложения; сервер проверяет сам.
 * Галочки до «Сохранить» — состояние экрана; сохраняется набор целиком.
 * Над ними — «Мои роли и права»: что о вошедшем знает сервер, видно и тому, кому консоль закрыта.
 */
export function AccessScreen({ vm, actions }: { vm: AccessVm; actions: AccessActions }) {
  if (!vm.manage.allowed) {
    return (
      <section aria-label="Права к ресурсам" className="platform-access">
        <StatusMessage kind="forbidden" title="Права к ресурсам" text={vm.manage.reason ?? undefined} />
        <MyAccessPanel mine={vm.mine} actions={actions} />
      </section>
    )
  }
  return (
    <section aria-labelledby="platform-access-title" className="platform-access">
      <h2 id="platform-access-title">Права к ресурсам</h2>
      <MyAccessPanel mine={vm.mine} actions={actions} />
      <RolesSummary summary={vm.summary} actions={actions} />
      <SelectField label="Роль" value={vm.role} options={vm.roles} onChange={actions.roleChanged} actionId="platform.access.role" />
      {vm.superRole ? (
        <>
          <p className="platform-access__note">
            <ShieldCheck size={16} aria-hidden="true" /> {vm.roleTitle} работает со всеми ресурсами и правами мимо
            допуска и матрицы. Ниже всё отмечено и заблокировано: снять права у этой роли нельзя, иначе экран прав
            мог бы стать недоступен всем.
          </p>
          <SuperRoleResources vm={vm} actions={actions} />
          <SuperRolePermissions vm={vm} actions={actions} />
        </>
      ) : (
        <>
          {/* Галочки начинаются с данных сервера один раз на роль: фоновое перечитывание их не сбрасывает. */}
          <RoleResources
            key={`resources:${vm.role}:${vm.granted.query.status === 'ready'}`}
            vm={vm}
            actions={actions}
          />
          <RolePermissions
            key={`permissions:${vm.role}:${vm.permissions.query.status === 'ready'}`}
            vm={vm}
            actions={actions}
          />
        </>
      )}
    </section>
  )
}

function toggle(list: readonly string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...list, value])] : list.filter((item) => item !== value)
}

/** Права словами через запятую, с числом: у вида их десятки, и без числа список не окинуть взглядом. */
function labels(items: readonly CapabilityRefVm[]): string {
  return `${items.length}: ${items.map((item) => item.label).join(', ')}`
}

/**
 * «Мои роли и права»: роли из токена и права, которые вернул сервер, — то, из чего экраны решают, что показать.
 * Пусто у человека почти всегда по одной из трёх причин, и панель называет их: в токене нет роли приложения,
 * роли не выдали права в матрице, право выключено у самого ресурса.
 */
function MyAccessPanel({ mine, actions }: { mine: MyAccessVm; actions: AccessActions }) {
  const [listed, setListed] = useState(false)
  return (
    <section aria-labelledby="platform-my-access-title" className="platform-access__mine">
      <h3 id="platform-my-access-title">Мои роли и права</h3>
      {mine.query.status !== 'ready' ? (
        <QueryState query={mine.query} emptyText="Права не получены" onRetry={actions.reload} />
      ) : (
        <>
          {mine.noRoles ? (
            <p className="platform-access__warn" role="status">
              <TriangleAlert size={16} aria-hidden="true" /> В вашем токене нет ролей приложения («administration:…»).
              Роли выдаёт Kalmykov ID: пока роль не выдана, прав нет и кнопки скрыты.
            </p>
          ) : (
            <p>
              Роли в токене:{' '}
              {mine.roles.map((role, at) => (
                <span key={role.value}>
                  {at > 0 && ', '}
                  <strong>{role.label}</strong>
                  {role.label !== role.value && <code className="platform-access__code">{role.value}</code>}
                </span>
              ))}
            </p>
          )}
          <p>
            {mine.superRole
              ? `Суперроль: все права каталога мимо матрицы — сервер вернул ${mine.total}.`
              : `Права, выданные вашим ролям: ${mine.total}.`}
          </p>
          {mine.groups.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => setListed(!listed)}
              aria-expanded={listed}
              actionId="platform.access.mine-list"
            >
              {listed ? 'Скрыть список прав' : `Показать список прав (${mine.total})`}
            </Button>
          )}
          {listed &&
            mine.groups.map((group) => (
              <fieldset key={group.group}>
                <legend>{group.title}</legend>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.key}>
                      {item.title} <code className="platform-access__code">{item.key}</code>
                    </li>
                  ))}
                </ul>
              </fieldset>
            ))}
          {mine.withheld.length > 0 && (
            <p className="platform-access__warn" role="status">
              <TriangleAlert size={16} aria-hidden="true" /> Каталог знает права, которых сервер вам не вернул (
              {mine.withheld.length}): {mine.withheld.map((item) => item.title).join(', ')}. Так выглядит личный запрет
              (<code>access_override</code> в токене) — он сильнее суперроли.
            </p>
          )}
          {mine.gaps.length > 0 && (
            <div className="platform-access__gaps">
              <h4>Права, не включённые у ресурсов</h4>
              <p className="platform-access__note">
                Включено ли право у самого ресурса, решает слой ниже матрицы: выключенное право недоступно никому, и
                суперроли тоже. Включает администратор в редакторе ресурса.
              </p>
              <ul>
                {mine.gaps.map((gap) => (
                  <li key={gap.id}>
                    <strong>{gap.name}</strong> · {gap.kindTitle}
                    {!gap.resourceEnabled && ': ресурс выключен целиком'}
                    {gap.absent.length > 0 && (
                      <>
                        <br />
                        Нет в списке прав ресурса (вид добавил позже создания), {labels(gap.absent)}
                      </>
                    )}
                    {gap.disabled.length > 0 && (
                      <>
                        <br />
                        Выключено, {labels(gap.disabled)}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Права всех ролей рядом: что сохранено в матрице прямо сейчас. Состояние не только значком: рядом слово «выдано»/«нет»
 * для чтения с экрана. После «Сохранить права» сводка перечитывается — так видно, что правка дошла до сервера.
 */
function RolesSummary({ summary, actions }: { summary: RolesSummaryVm; actions: AccessActions }) {
  return (
    <section aria-labelledby="platform-access-summary-title" className="platform-access__summary-section">
      <h3 id="platform-access-summary-title">Права всех ролей</h3>
      {summary.query.status !== 'ready' ? (
        <QueryState query={summary.query} emptyText="Каталог прав пуст" onRetry={actions.reload} />
      ) : (
        <div className="platform-access__summary-wrap">
          <table className="platform-access__summary">
            <caption className="platform-access__note">
              Что сохранено в матрице сейчас. Администратор приложения работает со всеми правами мимо матрицы.
            </caption>
            <thead>
              <tr>
                <th scope="col">Право</th>
                {summary.roles.map((role, at) => (
                  <th scope="col" key={role.value}>
                    {role.label}
                    <br />
                    <span className="platform-access__code">
                      {summary.totals[at] ?? 0} из {summary.catalogSize}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            {summary.groups.map((group) => (
              <tbody key={group.group}>
                <tr>
                  <th scope="rowgroup" colSpan={summary.roles.length + 1}>
                    {group.title}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.title}</th>
                    {row.granted.map((on, at) => (
                      <td key={summary.roles[at]?.value ?? at}>
                        {on ? <Check size={14} aria-hidden="true" /> : <Minus size={14} aria-hidden="true" />}
                        <span className="platform-access__sr">{on ? 'выдано' : 'нет'}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  )
}

function ResourceRowLabel({ row }: { row: AccessVm['resources']['rows'][number] }) {
  return (
    <>
      {row.enabled ? <CheckCircle2 size={14} aria-hidden="true" /> : <CircleOff size={14} aria-hidden="true" />}{' '}
      {row.name} · {row.kindTitle}
      {row.enabled ? '' : ' (выключен)'}
    </>
  )
}

function RoleResources({ vm, actions }: { vm: AccessVm; actions: AccessActions }) {
  const [selected, setSelected] = useState<readonly string[]>(vm.granted.ids)
  const loading = vm.resources.query.status !== 'ready' ? vm.resources.query : vm.granted.query
  return (
    <section aria-label="Допуск к ресурсам" className="platform-access__resources">
      <h3>Ресурсы роли «{vm.roleTitle}»</h3>
      {loading.status !== 'ready' ? (
        <QueryState query={loading} emptyText="Ресурсов нет" onRetry={actions.reload} />
      ) : vm.resources.rows.length === 0 ? (
        <p>Ресурсов в реестре нет</p>
      ) : (
        <ul>
          {vm.resources.rows.map((row) => (
            <li key={row.id}>
              <CheckboxField
                label={<ResourceRowLabel row={row} />}
                checked={selected.includes(row.id)}
                onChange={(on) => setSelected(toggle(selected, row.id, on))}
                actionId="platform.access.resource-check"
              />
            </li>
          ))}
        </ul>
      )}
      {vm.granted.hidden.length > 0 && (
        <p className="platform-access__note">
          Ещё {vm.granted.hidden.length} ресурс(ов) роли нет в вашем списке — их допуск сохранится без изменений.
        </p>
      )}
      <CommandError error={vm.saveResources.error} />
      {vm.saveResources.done && <p role="status">Допуск сохранён</p>}
      <Button
        variant="primary"
        icon={<Save size={16} aria-hidden="true" />}
        busy={vm.saveResources.busy}
        disabled={loading.status !== 'ready'}
        onClick={() => actions.saveResources([...new Set([...selected, ...vm.granted.hidden])])}
        actionId="platform.access.save-resources"
        disabledReason={loading.status !== 'ready' ? 'Список ресурсов ещё не загрузился.' : null}
      >
        Сохранить допуск
      </Button>
      <ActionReason action={vm.manage} />
    </section>
  )
}

/** Суперроль допущена ко всем ресурсам сама: список показывает это, но не даёт менять. */
function SuperRoleResources({ vm, actions }: { vm: AccessVm; actions: AccessActions }) {
  return (
    <section aria-label="Допуск к ресурсам" className="platform-access__resources">
      <h3>Ресурсы роли «{vm.roleTitle}»</h3>
      {vm.resources.query.status !== 'ready' ? (
        <QueryState query={vm.resources.query} emptyText="Ресурсов нет" onRetry={actions.reload} />
      ) : vm.resources.rows.length === 0 ? (
        <p>Ресурсов в реестре нет</p>
      ) : (
        <ul>
          {vm.resources.rows.map((row) => (
            <li key={row.id}>
              <CheckboxField
                label={<ResourceRowLabel row={row} />}
                checked
                disabled
                onChange={() => undefined}
                actionId="platform.access.resource-locked"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function RolePermissions({ vm, actions }: { vm: AccessVm; actions: AccessActions }) {
  const [selected, setSelected] = useState<readonly string[]>(vm.permissions.granted)
  const [filled, setFilled] = useState(false)
  const defaults = vm.permissions.defaults
  return (
    <section aria-label="Права роли" className="platform-access__permissions">
      <h3>Права роли «{vm.roleTitle}»</h3>
      {vm.permissions.query.status !== 'ready' ? (
        <QueryState query={vm.permissions.query} emptyText="Каталог прав пуст" onRetry={actions.reload} />
      ) : (
        vm.permissions.groups.map((group) => (
          <fieldset key={group.group}>
            <legend>{group.title}</legend>
            {group.items.map((item) => (
              <CheckboxField
                key={item.key}
                label={item.title}
                checked={selected.includes(item.key)}
                onChange={(on) => setSelected(toggle(selected, item.key, on))}
                actionId="platform.access.permission-check"
              />
            ))}
          </fieldset>
        ))
      )}
      <CommandError error={vm.savePermissions.error} />
      {vm.savePermissions.done && <p role="status">Права роли сохранены</p>}
      {filled && !vm.savePermissions.done && (
        <p role="status">Набор по умолчанию подставлен. Проверьте галочки и нажмите «Сохранить права».</p>
      )}
      <Button
        variant="secondary"
        icon={<ListRestart size={16} aria-hidden="true" />}
        disabled={defaults === null || vm.permissions.query.status !== 'ready'}
        onClick={() => {
          if (defaults === null) return
          setSelected(defaults)
          setFilled(true)
        }}
        actionId="platform.access.permission-defaults"
        disabledReason={defaults === null ? 'Набор роли ещё не загрузился.' : 'Список прав ещё не загрузился.'}
      >
        Подставить набор по умолчанию
      </Button>
      <Button
        variant="primary"
        icon={<Save size={16} aria-hidden="true" />}
        busy={vm.savePermissions.busy}
        disabled={vm.permissions.query.status !== 'ready'}
        onClick={() => actions.savePermissions(selected)}
        actionId="platform.access.save-permissions"
        disabledReason={vm.permissions.query.status !== 'ready' ? 'Список прав ещё не загрузился.' : null}
      >
        Сохранить права
      </Button>
    </section>
  )
}

/** Суперроль: весь каталог отмечен и заблокирован — права есть, и их видно, но снять нельзя. */
function SuperRolePermissions({ vm, actions }: { vm: AccessVm; actions: AccessActions }) {
  return (
    <section aria-label="Права роли" className="platform-access__permissions">
      <h3>Права роли «{vm.roleTitle}»</h3>
      {vm.permissions.query.status !== 'ready' ? (
        <QueryState query={vm.permissions.query} emptyText="Каталог прав пуст" onRetry={actions.reload} />
      ) : (
        vm.permissions.groups.map((group) => (
          <fieldset key={group.group}>
            <legend>{group.title}</legend>
            {group.items.map((item) => (
              <CheckboxField
                key={item.key}
                label={item.title}
                checked
                disabled
                onChange={() => undefined}
                actionId="platform.access.permission-locked"
              />
            ))}
          </fieldset>
        ))
      )}
    </section>
  )
}
